import { AccountInfo, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
// import { Client } from '@microsoft/microsoft-graph-client';
// import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser';
import { StorageCacheService } from './StorageCacheService';
import { ICourse } from '@/shared/ICourse';
import { isSameEmail } from '@/shared/parse';
import { runtimeConfig } from './RuntimeConfig';

const SCOPES = [
  "openid",
  "profile",
  "email",
  "offline_access",
  `api://${runtimeConfig.clientId}/Application`
];

// Debug user account
// @ts-ignore - kept for debugging purposes
const DEBUG_ACCOUNT: AccountInfo = {
  homeAccountId: 'debug-home-account-id',
  environment: 'debug',
  tenantId: 'debug-tenant-id',
  username: 'debug.user@debug.com',
  localAccountId: 'debug-local-account-id',
  name: 'Debug User',
  idTokenClaims: {
    email: 'debug.user@debug.com',
    name: 'Debug User',
    roles: ['admin', 'staff'], // Debug user has all roles
    aud: 'debug-audience',
    iss: 'debug-issuer',
    iat: Date.now() / 1000,
    nbf: Date.now() / 1000,
    exp: (Date.now() / 1000) + 3600,
    sub: 'debug-subject'
  }
};

export class AuthService {

  public static pca: IPublicClientApplication;
  public static account: AccountInfo | null = null;
  private static authenticationChanged: (() => void) | null = null;

  private static setAccount(account: AccountInfo | null) {
    this.account = account;
    this.authenticationChanged?.();
  }

  public static async configure(authenticationChanged: () => void) {

    this.authenticationChanged = authenticationChanged;

    this.pca = await PublicClientApplication.createPublicClientApplication({
      auth: {
        clientId: runtimeConfig.clientId,
        authority: `https://login.microsoftonline.com/${runtimeConfig.clientTenant}`,
        redirectUri: runtimeConfig.redirectUri,
      },
      cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
      },
    });

    this.setAccount(this.pca.getActiveAccount());

    this.pca.addEventCallback((message) => {
      if (['msal:loginSuccess', 'msal:loginFailure', 'msal:logoutSuccess', 'msal:logoutFailure'].includes(message.eventType)) {
        this.setAccount(AuthService.pca.getActiveAccount());
      }
    });
  }

  public static async getAccessToken() {

    for (let i = 0; i < 10; i++) {
      if (this.pca) {
        const result = await this.pca.acquireTokenSilent({ scopes: SCOPES });
        return result.accessToken;
      } else {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
  }

  public static get isAdmin() {
    return AuthService.isUserInRole('admin');
  }

  public static get isStaff() {
    return AuthService.isAdmin || AuthService.isUserInRole('staff');
  }

  public static isExternalExpert(course: ICourse | undefined) {
    return typeof course?.expert?.email === 'string' &&
      typeof this.account?.idTokenClaims?.email === 'string' && 
      isSameEmail(course.expert.email, this.account.idTokenClaims.email);
  }

  public static isUserInRole(role: string) {
    return this.account?.idTokenClaims?.roles?.includes(role);
  }

  public static async loginPopup() {

    this.setAccount(null);
    const authResult = await this.pca.loginPopup({
      scopes: SCOPES,
      prompt: "select_account"
    });
    this.pca.setActiveAccount(authResult.account);
    this.setAccount(authResult.account);
    return authResult.accessToken;
  }

  public static async logoutPopup() {

    StorageCacheService.resetCache();
    await this.pca.logoutPopup({
      account: this.account
    });
    this.authenticationChanged?.();
  }

  public static getCourseRoles(course?: ICourse) {
    return {
      isAdmin: AuthService.isAdmin,
      isStaff: AuthService.isStaff,
      isExternalExpert: AuthService.isExternalExpert(course),
    }
  }

}
